/**
 * The page an invitation or password-reset link lands on.
 *
 * WHY THIS EXISTS AT ALL. Firebase mints action links pointing at its own
 * handler on <project>.firebaseapp.com. That host is fixed per project, so a
 * customer of VTG - invited by an email branded Backup Beacon, about Vital
 * Technology Group - was being sent to a Google-owned domain they have never
 * heard of to choose a password. That is the exact shape of a phishing attempt,
 * and the most careful recipients are the ones most likely to refuse it.
 *
 * The link now arrives at the tenant's own address carrying the same oobCode,
 * and this page redeems it. The code is verified by the Auth backend through
 * the SDK - not by the page holding it - so serving our own page changes the
 * address and nothing about the security.
 *
 * MOUNTED ABOVE THE SIGN-IN GATE. Everybody who reaches here is by definition
 * signed out, and several of them have no password yet, so it cannot sit behind
 * the thing it exists to let people through.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  validatePassword,
  type PasswordValidationStatus,
  applyActionCode,
  checkActionCode,
} from 'firebase/auth';
import { auth, functions } from '../firebase';
import { Wordmark } from '../brand';

export const AUTH_ACTION_PATH = '/auth/action';

/**
 * Our own invitation path.
 *
 * Separate from /auth/action because the two carry different things: an action
 * link holds a Firebase oobCode minted when the mail was sent, an invite holds
 * OUR token and the Firebase code is fetched when it is opened. Same page, two
 * front doors.
 */
export const AUTH_INVITE_PATH = '/auth/invite';

/** True when the current URL is either, checked before any auth state. */
export function isAuthAction(): boolean {
  const p = window.location.pathname.replace(/\/+$/, '');
  return p === AUTH_ACTION_PATH || p === AUTH_INVITE_PATH;
}

type Phase =
  | { kind: 'checking' }
  | { kind: 'set-password'; email: string }
  | { kind: 'working' }
  | { kind: 'done'; heading: string; detail: string }
  | { kind: 'failed'; heading: string; detail: string };

/**
 * Firebase error codes, in the words of somebody who did not cause them.
 *
 * "auth/invalid-action-code" is the one that matters: it means the link is
 * expired OR already used, and Firebase cannot tell those apart. Saying
 * "invalid" alone invites people to retype it; naming both possibilities points
 * at the only thing that actually helps, which is asking for another.
 */
function explain(code: string): { heading: string; detail: string } {
  switch (code) {
    case 'auth/expired-action-code':
      return {
        heading: 'That link has expired',
        detail: 'Invitation links are only good for a short time. Ask whoever invited you to send another.',
      };
    case 'auth/invalid-action-code':
      return {
        heading: 'That link is no longer usable',
        detail:
          'It has either expired or already been used - a link works once. If you have not set a password yet, ask whoever invited you to send another.',
      };
    case 'auth/user-disabled':
      return {
        heading: 'This account is switched off',
        detail: 'Contact whoever manages your access.',
      };
    case 'auth/user-not-found':
      return {
        heading: 'That account no longer exists',
        detail: 'It may have been removed since the invitation was sent.',
      };
    /*
     * TWO CODES, not one. `weak-password` is the old six-character floor;
     * `password-does-not-meet-requirements` is what Identity Platform returns
     * once a password policy is set to "Require enforcement", which it now is.
     * Handling only the first meant a policy rejection fell through to the
     * generic dead end and told somebody to ask for a new invitation over a
     * password they could simply have retyped.
     *
     * No specifics in the text: the requirement list on the form is generated
     * from the live policy and is already correct, whereas a sentence here
     * would be a second copy of the rules waiting to go stale.
     */
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return {
        heading: 'That password does not meet the requirements',
        detail: 'Check the list under the password box and try again.',
      };
    default:
      return {
        heading: 'That did not work',
        detail: 'Try the link again. If it keeps failing, ask whoever invited you to send another.',
      };
  }
}

const codeOf = (e: unknown): string =>
  typeof e === 'object' && e !== null && 'code' in e ? String((e as { code: unknown }).code) : '';

/**
 * One exchange per token, per page load - NOT per component mount.
 *
 * A useRef guard only protects one instance. The production logs showed the
 * same token redeemed and then rejected as "used" 1.6 seconds later, which a
 * ref cannot explain and cannot prevent: anything that remounts this component
 * - a parent re-render that changes its position, a browser prerender, a
 * second tab, React strict mode - gets a fresh ref and spends the token again,
 * locking out the one person entitled to use it.
 *
 * A module-level promise cache is immune to all of that. The second caller
 * receives the FIRST call's promise, so there is only ever one request, and it
 * resolves to the same code for whoever asks.
 */
const exchanges = new Map<string, Promise<{ email: string; oobCode: string }>>();

function exchangeOnce(token: string): Promise<{ email: string; oobCode: string }> {
  const existing = exchanges.get(token);
  if (existing) return existing;

  const p = httpsCallable<{ token: string }, { email: string; oobCode: string }>(
    functions,
    'redeemInvite',
  )({ token }).then((r) => r.data);

  // A FAILED exchange is forgotten, so a genuine retry is still possible. Only
  // success is worth remembering - a cached rejection would turn one bad
  // network moment into a permanently dead link.
  p.catch(() => exchanges.delete(token));

  exchanges.set(token, p);
  return p;
}

/**
 * The password rules, read from the project rather than written down here.
 *
 * WHY NOT JUST HARDCODE "12 characters, a capital, a number, a symbol": because
 * the rule that matters is the one Identity Platform actually enforces, and a
 * second copy in the UI is a copy that goes stale the day somebody changes the
 * policy in GCP. Then the screen promises one thing and the server refuses for
 * another reason, which is the most confusing failure a sign-up form has.
 *
 * validatePassword() asks Firebase for the configured policy and reports which
 * criteria this password meets, so this list describes whatever is currently
 * enforced - including changes nobody told us about.
 */
interface Rule { label: string; met: boolean }

function rulesFrom(s: PasswordValidationStatus | null): Rule[] {
  if (!s) return [];
  const opts = s.passwordPolicy?.customStrengthOptions ?? {};
  const out: Rule[] = [];

  const min = opts.minPasswordLength;
  if (typeof min === 'number') {
    out.push({ label: `At least ${min} characters`, met: s.meetsMinPasswordLength === true });
  }
  if (opts.containsUppercaseLetter) {
    out.push({ label: 'A capital letter', met: s.containsUppercaseLetter === true });
  }
  if (opts.containsLowercaseLetter) {
    out.push({ label: 'A lower-case letter', met: s.containsLowercaseLetter === true });
  }
  if (opts.containsNumericCharacter) {
    out.push({ label: 'A number', met: s.containsNumericCharacter === true });
  }
  if (opts.containsNonAlphanumericCharacter) {
    out.push({ label: 'A symbol, such as ! ? or #', met: s.containsNonAlphanumericCharacter === true });
  }
  return out;
}

export function AuthAction() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode') ?? 'resetPassword';
  const inviteToken = params.get('t') ?? '';
  const isInvite = window.location.pathname.replace(/\/+$/, '') === AUTH_INVITE_PATH;

  // For an action link the code is in the URL; for an invitation it is fetched
  // in exchange for the token, so it starts empty and is filled in below.
  const [oobCode, setOobCode] = useState(params.get('oobCode') ?? '');
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [pwStatus, setPwStatus] = useState<PasswordValidationStatus | null>(null);

  /*
   * Validated on every keystroke, but the POLICY is fetched once and cached by
   * the SDK - so this is local arithmetic after the first call, not a request
   * per character. Run with an empty password on mount so the requirements are
   * on screen BEFORE anyone types: being told the rules after failing them is
   * the thing this is meant to stop.
   */
  useEffect(() => {
    let alive = true;
    void validatePassword(auth, pw)
      .then((s) => { if (alive) setPwStatus(s); })
      // A policy we cannot fetch must not block the form. The rules simply do
      // not render, and the server still enforces them.
      .catch(() => { if (alive) setPwStatus(null); });
    return () => { alive = false; };
  }, [pw]);

  const rules = rulesFrom(pwStatus);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      /*
       * AN INVITATION IS EXCHANGED, NOT READ.
       *
       * The token in the mail is ours and lasts 24 hours. Firebase's own code -
       * which lasts one hour and cannot be configured - is minted by the server
       * at this moment, with somebody actually here to use it. Its short life
       * is then spent in the next few seconds rather than in an inbox.
       *
       * The exchange is single-use, so this must not run twice - and in
       * production it DID, which is what exchangeOnce above exists to stop.
       * Strict mode was never the only way this happens; anything that
       * remounts the component is enough.
       */
      if (isInvite) {
        // No ref guard here: exchangeOnce is the guard, and unlike a ref it
        // survives a remount. See its comment for why that mattered.
        try {
          const data = await exchangeOnce(inviteToken);
          if (cancelled) return;
          setOobCode(data.oobCode);
          setPhase({ kind: 'set-password', email: data.email });
        } catch (e) {
          if (!cancelled) {
            setPhase({
              kind: 'failed',
              heading: 'That invitation is no longer usable',
              detail:
                (e as { message?: string })?.message ??
                'It may have expired, or already been used. Ask whoever invited you to send another.',
            });
          }
        }
        return;
      }

      if (!oobCode) {
        if (!cancelled) {
          setPhase({
            kind: 'failed',
            heading: 'This link is incomplete',
            detail: 'Some mail programs shorten long links. Try opening it from the original message.',
          });
        }
        return;
      }

      try {
        if (mode === 'resetPassword') {
          // Verify BEFORE showing the form. Asking somebody to choose a
          // password and only then telling them the link was dead is a worse
          // failure than telling them immediately.
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) setPhase({ kind: 'set-password', email });
          return;
        }

        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          if (!cancelled) {
            setPhase({
              kind: 'done',
              heading: 'Email address confirmed',
              detail: 'You can sign in now.',
            });
          }
          return;
        }

        // Anything else is a mode we do not handle. Check the code so the
        // message is accurate, then say plainly that this link is not for here.
        await checkActionCode(auth, oobCode);
        if (!cancelled) {
          setPhase({
            kind: 'failed',
            heading: 'This link is not one we handle',
            detail: 'Ask whoever sent it to you what it was for.',
          });
        }
      } catch (e) {
        if (!cancelled) setPhase({ kind: 'failed', ...explain(codeOf(e)) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, isInvite, inviteToken]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // Checked here rather than trusted to the browser: a mismatch caught after
    // the password is already set cannot be undone without another link.
    if (pw !== pw2) {
      setFormError('Those two passwords are not the same.');
      return;
    }
    /*
     * Checked against the PROJECT policy, not a number written here. The old
     * rule said eight characters, which had not been true since the policy was
     * tightened in GCP - so the form accepted a password the server then
     * refused, and the person was told nothing useful.
     *
     * Falls through when the policy cannot be read: the server is the real
     * gate, and refusing to submit because we could not fetch a rule list
     * would lock out somebody whose password is perfectly fine.
     */
    if (pwStatus && pwStatus.isValid === false) {
      const unmet = rulesFrom(pwStatus).filter((r) => !r.met).map((r) => r.label.toLowerCase());
      setFormError(
        unmet.length
          ? `That password still needs: ${unmet.join(', ')}.`
          : 'That password does not meet the requirements.',
      );
      return;
    }

    const email = phase.kind === 'set-password' ? phase.email : '';
    setPhase({ kind: 'working' });
    try {
      await confirmPasswordReset(auth, oobCode, pw);
      setPhase({
        kind: 'done',
        heading: 'Your password is set',
        detail: `Sign in as ${email} to open your dashboard.`,
      });
    } catch (e) {
      const { heading, detail } = explain(codeOf(e));
      // A password rejection is recoverable in place - send them back to the
      // form rather than to a dead end that needs a fresh invitation.
      const code = codeOf(e);
      if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        setPhase({ kind: 'set-password', email });
        setFormError(detail);
        return;
      }
      setPhase({ kind: 'failed', heading, detail });
    }
  }

  return (
    <div className="authaction">
      <div className="authaction-card">
        <div className="authaction-brand">
          <Wordmark width={168} />
        </div>

        {phase.kind === 'checking' && <p className="authaction-detail">Checking your link…</p>}

        {phase.kind === 'set-password' && (
          <>
            <h1 className="authaction-heading">Choose a password</h1>
            <p className="authaction-detail">
              For <strong>{phase.email}</strong>
            </p>
            <form onSubmit={(e) => void submit(e)}>
              <label className="authaction-label" htmlFor="pw">
                New password
              </label>
              <input
                id="pw"
                className="authaction-input"
                type="password"
                autoComplete="new-password"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
              {rules.length > 0 && (
                <ul className="pwreq" aria-label="Password requirements">
                  {rules.map((r) => (
                    <li key={r.label} className={r.met ? 'pwreq-met' : ''}>
                      {/* The tick is decorative - the text and the class carry
                          the meaning, so a screen reader is not left reading a
                          list of check marks with no state. */}
                      <span aria-hidden="true" className="pwreq-mark">{r.met ? '\u2713' : '\u25CB'}</span>
                      <span>{r.label}</span>
                      <span className="sr-only">{r.met ? ' - met' : ' - not yet met'}</span>
                    </li>
                  ))}
                </ul>
              )}

              <label className="authaction-label" htmlFor="pw2">
                Type it again
              </label>
              <input
                id="pw2"
                className="authaction-input"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
              {formError && <p className="authaction-error">{formError}</p>}
              <button className="authaction-button" type="submit">
                Set password
              </button>
            </form>
          </>
        )}

        {phase.kind === 'working' && <p className="authaction-detail">Setting your password…</p>}

        {(phase.kind === 'done' || phase.kind === 'failed') && (
          <>
            <h1 className="authaction-heading">{phase.heading}</h1>
            <p className="authaction-detail">{phase.detail}</p>
            <a className="authaction-button" href="/">
              Go to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
